import { useParams } from 'react-router'

/**
 * Resolve which scope the Custom Courses surfaces are operating in.
 *
 * The same components back three scopes, distinguished only by URL params:
 *   - admin    (no entityId / no organizationId) → global RescueX catalog
 *   - facility (entityId)                         → facility-owned + shared-in
 *   - org      (organizationId, RSCX-320)         → org-owned, shared to facilities
 *
 * `isCustom` groups facility + org: both author non-xverse "custom" content
 * (filtered to is_xverse=false, locked to the Custom Student certificate type,
 * no cost / certificate-PDF configuration) as opposed to the RescueX admin
 * catalog. Use `isCustom` for behaviour that's shared between facility and org,
 * and `isFacility` / `isOrg` only where the two genuinely diverge (request
 * routing, nav paths, provenance labels).
 *
 * `scopeArgs` is spread into the courses/certificates/course-assets service
 * calls — it carries whichever of entityId / organizationId is active so the
 * service resolves the right base path.
 */
export const useCoursesScope = () => {
  const { entityId, organizationId } = useParams()
  const isFacility = Boolean(entityId)
  const isOrg = Boolean(organizationId)
  const isCustom = isFacility || isOrg

  const hubBase = (() => {
    if (isOrg) return `/organizations/${organizationId}/courses`
    if (isFacility) return `/accounts/${entityId}/instructors/courses`
    return '/admin/courses'
  })()

  const rootCrumb = (() => {
    if (isOrg) return 'Organization'
    if (isFacility) return 'Instructor'
    return 'Admin'
  })()

  return {
    entityId,
    organizationId,
    isFacility,
    isOrg,
    isCustom,
    // Distinct cache key per scope so facility/org/admin lists don't collide.
    scopeId: entityId ?? organizationId ?? null,
    scopeArgs: isOrg ? { organizationId } : { entityId },
    hubBase,
    rootCrumb,
    // The certificate surface is labelled "Custom Courses" in custom scopes.
    certLabel: isCustom ? 'Custom Courses' : 'Courses',
  }
}
